import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronDown,
  ChevronUp,
  ExclamationTriangleFill,
  InfoCircle,
  PencilSquare,
  Trophy,
  XCircle,
} from 'react-bootstrap-icons';
import { useLocale } from '../i18n/LocaleContext';
import { fetchPrizeWheels, cancelPrizeWheel, fetchPrizeWheelWinners, startPrizeWheelDraw } from '../api/prizeWheels';
import { connectGiftDropsSocket } from '../api/giftDropsSocket';
import { getToken } from '../config/api';
import { isEmbedded } from '../config/embedded';
import {
  HISTORY_PAGE_SIZE,
  PRIZE_WHEEL_EVENTS,
  STATUSES,
  applyWheelEvent,
  currentWheel,
  errorText,
  isActive,
  isCancellable,
  isEditable,
  lockReasonKey,
  mergeFirstPage,
  nextRecheckAt,
  pollIntervalMs,
  sortNewestFirst,
  splitCountdown,
  wheelPhase,
} from './prizeWheelShape';
import WheelProof from './wheel/WheelProof';
import WheelFormModal from './wheel/WheelFormModal';
import WheelLiveStage from './wheel/WheelLiveStage';
import RoundResults from './wheel/RoundResults';
import FreeNotice from './wheel/FreeNotice';
import { fmtDateTime, fmtNum, useEscape, useNow } from './wheel/wheelUi';
import './GiftDropsTab.scss';
import './PrizeWheelTab.scss';

// Challenges / Játékok — the brand side of the prize wheel ("Szerencsekerék").
// The brand schedules a wheel; from then on it stands next to the brand's
// store on the Outfino map, people join it in the app, and at the draw time
// the server draws the winner(s) — fairly and verifiably (the seed's hash is
// published up front, the seed itself after the draw) — while every app plays
// the same spin. One scheduled / drawing wheel per brand at a time.
//
// Live via the gift:brand:<brandId> socket room (prize_wheel_* events) in the
// portal; the Shopify embedded app and a dropped socket poll instead.

const REFRESH_DEBOUNCE_MS = 300;
const RECHECK_AFTER_MS = 2500;

/** The wheel as a 2D glyph with the brand's logo on the hub — the card / table icon. */
export function WheelGlyph({ logo, name, size = 'md' }) {
  const segs = 12;
  return (
    <span className={`wheel-glyph wheel-glyph--${size}`} aria-hidden="true">
      <svg viewBox="-50 -50 100 100" className="wheel-glyph__svg">
        <circle r="47" className="wheel-glyph__rim" />
        {Array.from({ length: segs }, (_, i) => {
          const a0 = (i / segs) * Math.PI * 2;
          const a1 = ((i + 1) / segs) * Math.PI * 2;
          const r = 40;
          return (
            <path
              key={i}
              d={`M0 0 L${Math.sin(a0) * r} ${-Math.cos(a0) * r} A${r} ${r} 0 0 1 ${Math.sin(a1) * r} ${-Math.cos(a1) * r} Z`}
              className={i % 2 ? 'wheel-glyph__seg is-b' : 'wheel-glyph__seg'}
            />
          );
        })}
        <path d="M-6 -50 L6 -50 L0 -38 Z" className="wheel-glyph__pointer" />
      </svg>
      {size !== 'xs' && (
        <span className="wheel-glyph__logo">
          {logo ? <img src={logo} alt="" /> : <span>{name?.[0]?.toUpperCase() || 'B'}</span>}
        </span>
      )}
    </span>
  );
}

function StatusBadge({ status }) {
  const { t } = useLocale();
  const known = STATUSES.includes(status);
  return (
    <span className={`wheel-status is-${known ? status : 'other'}`}>
      {status === 'drawing' && <span className="wheel-status__dot" aria-hidden="true" />}
      {known ? t(`wheel.status.${status}`) : status}
    </span>
  );
}

function Person({ winner }) {
  const { t } = useLocale();
  const name = !winner || winner.anonymous || !winner.name ? t('wheel.someone') : winner.name;
  const letter = (winner && !winner.anonymous && (winner.name || winner.username || '').trim().charAt(0).toUpperCase()) || '?';
  return (
    <span className="gift-person">
      <span className="gift-person__avatar" aria-hidden="true">
        <span>{letter}</span>
        {winner?.avatarUrl && (
          <img src={winner.avatarUrl} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        )}
      </span>
      <span className="gift-person__name">{name}</span>
      {winner?.username && winner.username !== name && <span className="gift-muted">@{winner.username}</span>}
    </span>
  );
}

function Stat({ label, children, live = false, wide = false }) {
  return (
    <div className={`wheel-stat${wide ? ' is-wide' : ''}`}>
      <div className="wheel-stat__label">
        {live && <span className="wheel-stat__live" aria-hidden="true" />}
        {label}
      </div>
      <div className="wheel-stat__value">{children}</div>
    </div>
  );
}

function Countdown({ ms }) {
  const { t } = useLocale();
  const { days, hms } = splitCountdown(ms);
  return days > 0 ? t('wheel.countdownDays', { d: days, hms }) : hms;
}

function CurrentWheelCard({ wheel, brand, nowMs, serverNow, canEdit, winners, winnersState, onEdit, onCancel, stageAllowed, start }) {
  const { t, lang } = useLocale();
  const { phase, remainingMs, toDrawMs } = wheelPhase(wheel, nowMs);
  const editable = isEditable(wheel, nowMs);
  const lockKey = lockReasonKey(wheel, nowMs);
  const finished = wheel.status === 'finished';
  const onStage = phase === 'ready' || phase === 'drawing' || finished;
  return (
    <div className={`wheel-card is-${phase}`}>
      <div className="wheel-card__top">
        <WheelGlyph logo={wheel.brand?.logoUrl || brand.logo} name={brand.name} />
        <div className="wheel-card__head">
          <div className="wheel-card__badges">
            <StatusBadge status={wheel.status} />
            {phase === 'opensSoon' && (
              <span className="wheel-card__chip">{t('wheel.opensAtChip', { at: fmtDateTime(wheel.opensAt, lang) })}</span>
            )}
            {phase === 'open' && <span className="wheel-card__chip is-open">{t('wheel.joinOpen')}</span>}
            {wheel.eligibility && <span className="wheel-card__chip">{t(`wheel.eligibility.${wheel.eligibility}`)}</span>}
            {wheel.winnerCount > 1 && (
              <span className="wheel-card__chip">{t(wheel.excludePreviousWinners ? 'wheel.excludeOn' : 'wheel.excludeOff')}</span>
            )}
          </div>
          <h3 className="wheel-card__title">{wheel.title || '—'}</h3>
          {wheel.prizeDescription && (
            <p className="wheel-card__prize">
              <Trophy size={13} aria-hidden="true" /> {wheel.prizeDescription}
            </p>
          )}
        </div>
        {canEdit && isCancellable(wheel) && phase !== 'ready' && (
          <div className="wheel-card__actions">
            <button
              type="button"
              className="btn-o btn-o--ghost btn-o--sm"
              onClick={() => onEdit(wheel)}
              disabled={!editable}
              title={editable ? undefined : t(lockKey || 'wheel.lock.generic')}
            >
              <PencilSquare size={14} /> {t('wheel.edit')}
            </button>
            <button type="button" className="btn-o btn-o--ghost btn-o--sm wheel-card__cancel" onClick={() => onCancel(wheel)}>
              <XCircle size={14} /> {t('wheel.cancel')}
            </button>
          </div>
        )}
      </div>

      {onStage && stageAllowed && (
        <WheelLiveStage
          wheel={wheel}
          brand={brand}
          serverNow={serverNow}
          ready={phase === 'ready' ? { remainingMs, onStart: () => start.run(wheel), starting: start.busy, error: start.error, canStart: canEdit } : null}
        />
      )}

      <div className="wheel-card__stats">
        {phase === 'ready' ? (
          <Stat label={t('wheel.stat.autoStart')} wide>
            <span className="wheel-card__time">{splitCountdown(remainingMs).hms.replace(/^00:/, '')}</span>
            <span className="wheel-card__sub">{t('wheel.readyNow')}</span>
          </Stat>
        ) : phase === 'drawing' ? (
          <Stat label={t('wheel.stat.spinEnds')} wide>
            <span className="wheel-card__time">{splitCountdown(remainingMs).hms.replace(/^00:/, '')}</span>
            <span className="wheel-card__sub">{t('wheel.drawingNow')}</span>
          </Stat>
        ) : finished ? (
          <Stat label={t('wheel.stat.drawnAt')} wide>
            <span className="wheel-card__time is-date">{fmtDateTime(wheel.finishedAt ?? wheel.drawAt, lang, { weekday: true })}</span>
          </Stat>
        ) : (
          <Stat label={t('wheel.stat.untilDraw')} wide>
            <span className="wheel-card__time"><Countdown ms={toDrawMs} /></span>
            <span className="wheel-card__sub">{fmtDateTime(wheel.drawAt, lang, { weekday: true })}</span>
          </Stat>
        )}
        <Stat label={t('wheel.stat.participants')} live={wheel.status === 'scheduled'}>
          {fmtNum(wheel.participantCount, lang)}
        </Stat>
        <Stat label={t('wheel.stat.rounds')}>{fmtNum(wheel.winnerCount, lang)}</Stat>
        {phase === 'opensSoon' && (
          <Stat label={t('wheel.stat.opensIn')}>
            <Countdown ms={remainingMs} />
          </Stat>
        )}
      </div>

      {canEdit && lockKey && phase !== 'ready' && (
        <div className="wheel-card__lock">
          <InfoCircle size={13} aria-hidden="true" /> {t(lockKey)}
        </div>
      )}

      <div className="wheel-card__section">
        <div className="wheel-card__section-title">{t(finished ? 'wheel.resultsTitle' : 'wheel.prizesTitle')}</div>
        <RoundResults wheel={wheel} winners={winners} state={winnersState} showWinners={finished} />
      </div>

      {!finished && <FreeNotice compact />}

      <WheelProof wheel={wheel} winners={winners} />
    </div>
  );
}

function CancelWheelModal({ brand, wheel, onClose, onDone }) {
  const { t, lang } = useLocale();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEscape(onClose, busy);

  async function submit() {
    setBusy(true);
    setError('');
    try {
      const { wheel: updated } = await cancelPrizeWheel(brand.id, wheel.id);
      onDone(updated ?? { ...wheel, status: 'cancelled' });
    } catch (err) {
      setError(errorText(err, t));
      setBusy(false);
    }
  }

  return createPortal(
    <div className="gift-modal" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="gift-modal__dialog gift-modal__dialog--small" role="alertdialog" aria-modal="true" aria-labelledby="wheel-cancel-title">
        <div className="gift-modal__head">
          <h3 id="wheel-cancel-title" className="gift-modal__title">{t('wheel.cancelTitle')}</h3>
        </div>
        <p className="gift-modal__text">
          {t('wheel.cancelText', { title: wheel.title, at: fmtDateTime(wheel.drawAt, lang) })}
          {wheel.participantCount > 0 && <> {t('wheel.cancelJoined', { n: fmtNum(wheel.participantCount, lang) })}</>}
        </p>
        {error && <div className="gift-modal__error" role="alert">{error}</div>}
        <div className="gift-modal__actions">
          <button type="button" className="btn-o btn-o--ghost" onClick={onClose} disabled={busy}>
            {t('wheel.form.back')}
          </button>
          <button type="button" className="btn-o gift-btn--danger" onClick={submit} disabled={busy}>
            {busy ? t('wheel.cancelling') : t('wheel.cancelConfirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function WinnersCell({ wheel, winners }) {
  const { t } = useLocale();
  if (wheel.status !== 'finished') return <span className="gift-muted">—</span>;
  if (!winners) return <span className="gift-muted">{t('wheel.winnersN', { n: wheel.winnerCount })}</span>;
  if (!winners.length) return <span className="gift-muted">{t('wheel.noWinnersShort')}</span>;
  return (
    <span className="wheel-winners-cell">
      <Person winner={winners[0]} />
      {winners.length > 1 && <span className="wheel-more">+{winners.length - 1}</span>}
    </span>
  );
}

export default function PrizeWheelTab({ brand, canEdit = false }) {
  const { t, lang } = useLocale();

  const [list, setList] = useState({ wheels: [], nextBefore: null });
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [winnersById, setWinnersById] = useState({}); // id → { state, winners, seed? }
  const [expanded, setExpanded] = useState(null);

  const [form, setForm] = useState(null); // null | { wheel: null } (create) | { wheel } (edit)
  const [cancelTarget, setCancelTarget] = useState(null);
  const [notice, setNotice] = useState('');

  // Server clock − client clock, from serverNowMs (midpoint of the request).
  const offsetRef = useRef(0);
  const serverNow = useCallback(() => Date.now() + offsetRef.current, []);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const load = useCallback(async () => {
    try {
      const sentAt = Date.now();
      const page = await fetchPrizeWheels(brand.id, { limit: HISTORY_PAGE_SIZE });
      if (!alive.current) return;
      if (page.serverNowMs !== null) offsetRef.current = page.serverNowMs - Math.round((sentAt + Date.now()) / 2);
      setList((prev) => ({
        wheels: mergeFirstPage(prev.wheels, page.wheels),
        nextBefore: prev.wheels.length > page.wheels.length ? prev.nextBefore : page.nextBefore,
      }));
      setLoadError(null);
    } catch (err) {
      if (alive.current) setLoadError(err);
    } finally {
      if (alive.current) setLoaded(true);
    }
  }, [brand.id]);

  useEffect(() => { load(); }, [load]);

  const refreshTimer = useRef(null);
  const scheduleRefresh = useCallback((delay = REFRESH_DEBOUNCE_MS) => {
    clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(load, delay);
  }, [load]);
  useEffect(() => () => clearTimeout(refreshTimer.current), []);

  // Live: the brand's room on /style-universe (portal only).
  const [connected, setConnected] = useState(false);
  const connRef = useRef(null);
  useEffect(() => {
    if (isEmbedded()) return undefined;
    const conn = connectGiftDropsSocket({
      token: getToken(),
      brandId: brand.id,
      events: PRIZE_WHEEL_EVENTS,
      onConnectionChanged: (up) => {
        if (!alive.current) return;
        setConnected(up);
        if (up) scheduleRefresh(0);
      },
      onEvent: (name, data) => {
        if (!alive.current) return;
        const ms = Number(data?.serverNowMs);
        if (Number.isFinite(ms)) offsetRef.current = ms - Date.now();
        setList((prev) => ({ ...prev, wheels: applyWheelEvent(prev.wheels, name, data) }));
        // A count tick needs no re-read; everything else does.
        if (name !== 'prize_wheel_count') scheduleRefresh();
      },
    });
    connRef.current = conn;
    return () => {
      connRef.current = null;
      conn.disconnect();
    };
  }, [brand.id, scheduleRefresh]);

  // "Sorsolás indítása": over the socket (api/prizeWheels.js startPrizeWheelDraw), REST without one.
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState('');
  const runStart = useCallback(async (w) => {
    setStarting(true);
    setStartError('');
    try {
      const { wheel: next, serverNowMs } = await startPrizeWheelDraw({ brandId: brand.id, wheelId: w.id, socket: connRef.current });
      if (!alive.current) return;
      if (serverNowMs != null) offsetRef.current = serverNowMs - Date.now();
      if (next) setList((prev) => ({ ...prev, wheels: sortNewestFirst([next, ...prev.wheels.filter((x) => x.id !== next.id)]) }));
      scheduleRefresh(0);
    } catch (err) {
      if (alive.current) setStartError(errorText(err, t));
      scheduleRefresh(0);
    } finally {
      if (alive.current) setStarting(false);
    }
  }, [brand.id, scheduleRefresh, t]);

  const clientNow = useNow(1000);
  const now = clientNow + offsetRef.current;
  const current = currentWheel(list.wheels, now);
  const history = list.wheels.filter((w) => w !== current);
  const activeExists = isActive(current);

  // Polling while the socket is down: fast while the spin plays, 10 s while
  // scheduled (the live participant count), 30 s otherwise; paused in the background.
  const pollMs = pollIntervalMs(current);
  useEffect(() => {
    if (connected) return undefined;
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      load();
    }, pollMs);
    return () => clearInterval(id);
  }, [connected, pollMs, load]);

  // Read again just after the draw time / the end of the spin.
  const recheckAt = nextRecheckAt(current);
  useEffect(() => {
    if (recheckAt === null || recheckAt === undefined) return undefined;
    const wait = Math.max(500, recheckAt - (Date.now() + offsetRef.current) + RECHECK_AFTER_MS);
    if (wait > 2 ** 31 - 1) return undefined;
    const id = setTimeout(load, wait);
    return () => clearTimeout(id);
  }, [recheckAt, load]);

  // Winners of finished wheels: from the list when it carries them, else GET /winners.
  const loadWinners = useCallback(async (wheel) => {
    setWinnersById((m) => ({ ...m, [wheel.id]: { state: 'loading' } }));
    try {
      const r = await fetchPrizeWheelWinners(brand.id, wheel.id);
      if (!alive.current) return;
      setWinnersById((m) => ({ ...m, [wheel.id]: { state: 'ok', winners: r.winners, seed: r.seed } }));
    } catch {
      if (alive.current) setWinnersById((m) => ({ ...m, [wheel.id]: { state: 'error' } }));
    }
  }, [brand.id]);

  const winnersOf = (w) => w.winners ?? winnersById[w.id]?.winners ?? null;
  const winnersStateOf = (w) => (w.winners ? 'ok' : winnersById[w.id]?.state ?? 'idle');
  const withSeed = (w) => (w.seed || !winnersById[w.id]?.seed ? w : { ...w, seed: winnersById[w.id].seed });

  const needWinners = [current, history.find((w) => w.id === expanded)].filter(
    (w) => w && w.status === 'finished' && !w.winners && !winnersById[w.id]
  );
  useEffect(() => {
    needWinners.forEach(loadWinners);
  }, [needWinners.map((w) => w.id).join(','), loadWinners]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadMore() {
    if (!list.nextBefore || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await fetchPrizeWheels(brand.id, { limit: HISTORY_PAGE_SIZE, before: list.nextBefore });
      if (!alive.current) return;
      setList((prev) => {
        const ids = new Set(prev.wheels.map((w) => w.id));
        return { wheels: [...prev.wheels, ...page.wheels.filter((w) => !ids.has(w.id))], nextBefore: page.nextBefore };
      });
      setLoadError(null);
    } catch (err) {
      if (alive.current) setLoadError(err);
    } finally {
      if (alive.current) setLoadingMore(false);
    }
  }

  function onSaved({ wheel, serverNowMs }, edited) {
    setForm(null);
    setNotice(t(edited ? 'wheel.updated' : 'wheel.scheduled'));
    if (serverNowMs != null) offsetRef.current = serverNowMs - Date.now();
    if (wheel) setList((prev) => ({ ...prev, wheels: sortNewestFirst([wheel, ...prev.wheels.filter((w) => w.id !== wheel.id)]) }));
    load();
  }

  function onCancelled(wheel) {
    setCancelTarget(null);
    setNotice(t('wheel.cancelled'));
    setList((prev) => ({ ...prev, wheels: prev.wheels.map((w) => (w.id === wheel.id ? { ...w, ...wheel, status: 'cancelled' } : w)) }));
    load();
  }

  const closeForm = useCallback(() => setForm(null), []);
  const closeCancel = useCallback(() => setCancelTarget(null), []);

  return (
    <div className="panel-card gifts wheels">
      <div className="panel-card__row">
        <div>
          <h2 className="panel-card__title">{t('wheel.title')}</h2>
          <p className="gifts__lead">{t('wheel.lead')}</p>
        </div>
        {/* One scheduled / drawing wheel at a time: while there is one, the line below says why the button is gone. */}
        {canEdit && !activeExists && (
          <button
            type="button"
            className="btn-o btn-o--brand"
            onClick={() => { setNotice(''); setForm({ wheel: null }); }}
          >
            <Trophy size={15} style={{ marginRight: 8 }} />
            {t('wheel.scheduleButton')}
          </button>
        )}
      </div>

      {notice && <div className="gifts__notice" role="status">{notice}</div>}
      {canEdit && activeExists && (
        <div className="wheels__one">
          <InfoCircle size={14} aria-hidden="true" /> {t('wheel.oneAtATime')}
        </div>
      )}

      <div className="gifts__subhead">
        <span>{t('wheel.currentTitle')}</span>
        <span
          className={`gifts__poll${connected ? ' is-live' : ''}`}
          title={connected ? t('gifts.liveTitle') : t('wheel.pollingTitle')}
        >
          <span className="gifts__poll-dot" aria-hidden="true" />
          {connected ? t('gifts.live') : t('gifts.polling', { s: pollMs / 1000 })}
        </span>
      </div>

      {loadError && (
        <div className="gifts__error" role="alert">
          <ExclamationTriangleFill size={14} /> {errorText(loadError, t)}
          <button type="button" className="gifts__link" onClick={load}>{t('gifts.retry')}</button>
        </div>
      )}
      {!loaded ? (
        <div className="panel-card__muted">{t('shell.loading')}</div>
      ) : current ? (
        <CurrentWheelCard
          wheel={withSeed(current)}
          brand={brand}
          nowMs={now}
          serverNow={serverNow}
          canEdit={canEdit}
          stageAllowed={!form && !cancelTarget}
          start={{ run: runStart, busy: starting, error: startError }}
          winners={winnersOf(current)}
          winnersState={winnersStateOf(current)}
          onEdit={(w) => { setNotice(''); setForm({ wheel: w }); }}
          onCancel={setCancelTarget}
        />
      ) : (
        !loadError && (
          <div className="gifts__empty wheels__empty">
            <WheelGlyph logo={brand.logo} name={brand.name} size="sm" />
            <span>{t('wheel.noActive')}</span>
          </div>
        )
      )}

      <div className="gifts__subhead gifts__subhead--history">
        <span>{t('wheel.historyTitle')}</span>
      </div>
      {!loaded ? (
        <div className="panel-card__muted">{t('shell.loading')}</div>
      ) : history.length === 0 ? (
        !loadError && <div className="panel-card__muted">{t('wheel.noHistory')}</div>
      ) : (
        <div className="gifts__table-wrap">
          <table className="gifts__table wheels__table">
            <thead>
              <tr>
                <th>{t('wheel.col.drawAt')}</th>
                <th>{t('wheel.col.wheel')}</th>
                <th>{t('wheel.col.status')}</th>
                <th className="is-num">{t('wheel.col.participants')}</th>
                <th>{t('wheel.col.winners')}</th>
                <th aria-label={t('wheel.col.details')} />
              </tr>
            </thead>
            <tbody>
              {history.map((w) => {
                const open = expanded === w.id;
                return (
                  <React.Fragment key={w.id}>
                    <tr className={open ? 'is-open' : undefined}>
                      <td className="gift-nowrap wheels__c-date">{fmtDateTime(w.drawAt, lang)}</td>
                      <td className="wheels__c-title">
                        <div className="wheels__row-title">{w.title || '—'}</div>
                        {(() => {
                          const note = w.prizeDescription || w.rounds.map((r) => r.products.map((p) => p.name).join(', ')).filter(Boolean).join(' · ');
                          return note ? <div className="gifts__row-note" title={note}>{note}</div> : null;
                        })()}
                      </td>
                      <td className="wheels__c-status"><StatusBadge status={w.status} /></td>
                      <td className="is-num gift-nowrap wheels__c-count" data-label={t('wheel.col.participants')}>{w.status === 'cancelled' && !w.participantCount ? '—' : fmtNum(w.participantCount, lang)}</td>
                      <td className={`wheels__c-winners${w.status === 'finished' ? '' : ' is-empty'}`} data-label={t('wheel.col.winners')}><WinnersCell wheel={w} winners={winnersOf(w)} /></td>
                      <td className="wheels__toggle-cell">
                        <button
                          type="button"
                          className="wheels__toggle"
                          aria-expanded={open}
                          onClick={() => setExpanded(open ? null : w.id)}
                        >
                          {t('wheel.details')} {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        </button>
                      </td>
                    </tr>
                    {open && (
                      <tr className="wheels__detail">
                        <td colSpan={6}>
                          <div className="wheels__detail-grid">
                            {w.status === 'finished' && (
                              <div>
                                <div className="wheel-card__section-title">{t('wheel.resultsTitle')}</div>
                                <RoundResults wheel={w} winners={winnersOf(w)} state={winnersStateOf(w)} />
                              </div>
                            )}
                            {w.status === 'cancelled' ? (
                              <p className="gift-muted wheels__detail-note">
                                {t('wheel.cancelledAt', { at: fmtDateTime(w.cancelledAt ?? w.drawAt, lang) })}
                              </p>
                            ) : (
                              <WheelProof wheel={withSeed(w)} winners={winnersOf(w)} />
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {list.nextBefore && (
        <div className="gifts__more">
          <button type="button" className="btn-o btn-o--ghost btn-o--sm" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? t('shell.loading') : t('gifts.loadMore')}
          </button>
        </div>
      )}

      {form && (
        <WheelFormModal
          brand={brand}
          wheel={form.wheel}
          serverNow={serverNow}
          onClose={closeForm}
          onDone={onSaved}
          onConflict={load}
        />
      )}
      {cancelTarget && <CancelWheelModal brand={brand} wheel={cancelTarget} onClose={closeCancel} onDone={onCancelled} />}
    </div>
  );
}
